import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  type BookShiftBody,
  type CreateShiftBody,
  type FetchShiftsBody,
  type Shift,
  type ShiftsMetaResponse,
  type SubstituteShiftBody,
  shiftsService,
} from "@/services/shiftsService"

export const useShifts = (filters: FetchShiftsBody) => {
  return useQuery({
    queryKey: ["shifts", filters],
    queryFn: async () => {
      const res = await shiftsService.fetchShifts(filters)
      return res.results
    },
    enabled: Boolean(filters?.startTime && filters?.endTime && filters?.location),
  })
}

export const useCreateShift = () => {
  const qc = useQueryClient()
  return useMutation<Shift, Error, CreateShiftBody>({
    mutationFn: (body) => shiftsService.createShift(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shifts"] })
    },
  })
}

export const useShiftsMeta = () => {
  return useQuery<ShiftsMetaResponse>({
    queryKey: ["shifts-meta"],
    queryFn: () => shiftsService.fetchMeta(),
    staleTime: 1000 * 60 * 10, // 10 minutes
  })
}

// Driver-specific hooks
export const useAvailableShifts = () => {
  return useQuery<Shift[]>({
    queryKey: ["driver-available"],
    queryFn: async () => {
      const res = await shiftsService.fetchAvailableShifts()
      return res.results
    },
  })
}

export const useUpcomingShifts = () => {
  return useQuery<Shift[]>({
    queryKey: ["driver-upcoming"],
    queryFn: async () => {
      const res = await shiftsService.fetchUpcomingShifts()
      return res.results
    },
  })
}

export const usePastShifts = () => {
  return useQuery<Shift[]>({
    queryKey: ["driver-past"],
    queryFn: async () => {
      const res = await shiftsService.fetchPastShifts()
      return res.results
    },
  })
}

export const useBookShift = () => {
  const qc = useQueryClient()
  return useMutation<Shift, Error, BookShiftBody>({
    mutationFn: (body) => shiftsService.bookShift(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["driver-available"] })
      qc.invalidateQueries({ queryKey: ["driver-upcoming"] })
    },
  })
}

export const useSubstituteShift = () => {
  const qc = useQueryClient()
  return useMutation<Shift, Error, SubstituteShiftBody>({
    mutationFn: (body) => shiftsService.substituteShift(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["driver-upcoming"] })
      qc.invalidateQueries({ queryKey: ["driver-available"] })
    },
  })
}
